import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Lock, CheckCircle, AlertCircle, Check, X, Eye, EyeOff } from 'lucide-react';
import apiService from '@/services/api';

// Password strength calculator
const calculatePasswordStrength = (password) => {
  if (!password) return { score: 0, label: '', color: '', width: '0%' };
  
  let score = 0;
  const checks = {
    length: password.length >= 8,
    lengthBonus: password.length >= 12,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    numbers: /[0-9]/.test(password),
    symbols: /[^A-Za-z0-9]/.test(password),
  };

  if (checks.length) score += 1;
  if (checks.lengthBonus) score += 1;
  if (checks.lowercase) score += 1;
  if (checks.uppercase) score += 1;
  if (checks.numbers) score += 1;
  if (checks.symbols) score += 1;

  // Determine strength level
  if (score <= 2) {
    return { score, label: 'Weak', color: 'bg-red-500', textColor: 'text-red-600', width: '33%', checks };
  } else if (score <= 4) {
    return { score, label: 'Medium', color: 'bg-yellow-500', textColor: 'text-yellow-600', width: '66%', checks };
  } else {
    return { score, label: 'Strong', color: 'bg-green-500', textColor: 'text-green-600', width: '100%', checks };
  }
};

export default function ChangePasswordPage() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Calculate password strength
  const passwordStrength = useMemo(() => calculatePasswordStrength(newPassword), [newPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setMessage({ type: 'error', text: 'Please fill in all fields' });
      return;
    }

    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: 'New password must be at least 8 characters long' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    setLoading(true);

    try {
      const response = await apiService.changePassword({
        current_password: currentPassword,
        new_password: newPassword
      });

      setMessage({ type: 'success', text: 'Password changed successfully!' });
      
      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const errorMsg = error.message || 'Failed to change password. Please check your current password.';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const PasswordRequirement = ({ met, text }) => (
    <div className={`flex items-center gap-2 text-xs transition-colors ${met ? 'text-green-600' : 'text-slate-500'}`}>
      {met ? (
        <Check className="h-3.5 w-3.5 text-green-500" />
      ) : (
        <X className="h-3.5 w-3.5 text-slate-400" />
      )}
      <span>{text}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-800">Account Settings</h1>
          <p className="text-slate-600 mt-2">Manage your account security</p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Lock className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <CardTitle>Change Password</CardTitle>
                <CardDescription>Update your password to keep your account secure</CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {message.text && (
                <Alert variant={message.type === 'error' ? 'destructive' : 'default'} 
                       className={message.type === 'success' ? 'bg-green-50 border-green-200' : ''}>
                  <div className="flex items-center gap-2">
                    {message.type === 'success' ? 
                      <CheckCircle className="h-4 w-4 text-green-600" /> : 
                      <AlertCircle className="h-4 w-4" />
                    }
                    <AlertDescription className={message.type === 'success' ? 'text-green-800' : ''}>
                      {message.text}
                    </AlertDescription>
                  </div>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="currentPassword">Current Password</Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter your current password"
                    disabled={loading}
                    required
                    className="pr-10"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    tabIndex={-1}
                  >
                    {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter your new password"
                    disabled={loading}
                    required
                    className="pr-10"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    tabIndex={-1}
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                
                {/* Password Strength Indicator */}
                {newPassword && (
                  <div className="space-y-2 mt-3 p-3 bg-slate-50 rounded-lg border" data-testid="password-strength-indicator">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-slate-600">Password Strength</span>
                      <span className={`text-xs font-semibold ${passwordStrength.textColor}`} data-testid="password-strength-label">
                        {passwordStrength.label}
                      </span>
                    </div>
                    
                    {/* Strength Bar */}
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-300 ease-out ${passwordStrength.color}`}
                        style={{ width: passwordStrength.width }}
                        data-testid="password-strength-bar"
                      />
                    </div>

                    {/* Requirements Checklist */}
                    <div className="grid grid-cols-2 gap-1.5 mt-2">
                      <PasswordRequirement 
                        met={passwordStrength.checks?.length} 
                        text="At least 8 characters" 
                      />
                      <PasswordRequirement 
                        met={passwordStrength.checks?.uppercase} 
                        text="Uppercase letter" 
                      />
                      <PasswordRequirement 
                        met={passwordStrength.checks?.lowercase} 
                        text="Lowercase letter" 
                      />
                      <PasswordRequirement 
                        met={passwordStrength.checks?.numbers} 
                        text="Number" 
                      />
                      <PasswordRequirement 
                        met={passwordStrength.checks?.symbols} 
                        text="Special character" 
                      />
                      <PasswordRequirement 
                        met={passwordStrength.checks?.lengthBonus} 
                        text="12+ characters (bonus)" 
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your new password"
                    disabled={loading}
                    required
                    className={`pr-10 ${confirmPassword && (confirmPassword === newPassword ? 'border-green-500 focus-visible:ring-green-500' : 'border-red-500 focus-visible:ring-red-500')}`}
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {confirmPassword && (
                  <p className={`text-xs flex items-center gap-1 ${confirmPassword === newPassword ? 'text-green-600' : 'text-red-600'}`}>
                    {confirmPassword === newPassword ? (
                      <>
                        <Check className="h-3 w-3" /> Passwords match
                      </>
                    ) : (
                      <>
                        <X className="h-3 w-3" /> Passwords do not match
                      </>
                    )}
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  type="submit" 
                  disabled={loading || (newPassword && passwordStrength.label === 'Weak')}
                  className="flex-1"
                  data-testid="change-password-submit"
                >
                  {loading ? 'Changing Password...' : 'Change Password'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => {
                    setNewPassword('');
                    setConfirmPassword('');
                    setMessage({ type: '', text: '' });
                  }}
                  disabled={loading}
                  data-testid="change-password-cancel"
                >
                  Cancel
                </Button>
              </div>
            </form>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-sm text-blue-900 mb-2">Tips for a strong password:</h3>
              <ul className="text-xs text-blue-700 space-y-1">
                <li>• Use at least 12 characters for maximum security</li>
                <li>• Mix uppercase, lowercase, numbers, and symbols</li>
                <li>• Avoid common words or personal information</li>
                <li>• Don't reuse passwords from other accounts</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
